import { useState, useRef } from 'react'
import { LuCircleCheck, LuDownload, LuX, LuCopy, LuLoader } from 'react-icons/lu'
import './TransferSuccessModal.css'

/**
 * Modal de éxito para transferencias con opción de descargar comprobante PDF
 * El COELSA ID ya viene en transferData (se obtiene antes de mostrar el modal)
 * 
 * @param {boolean} isOpen - Si el modal está abierto
 * @param {function} onClose - Función para cerrar el modal
 * @param {Object} transferData - Datos de la transferencia (incluye coelsaId)
 */
function TransferSuccessModal({ isOpen, onClose, transferData }) {
  const [downloading, setDownloading] = useState(false)
  const [copied, setCopied] = useState(false)
  const comprobanteRef = useRef(null)
  
  // El COELSA ID viene directamente en transferData
  const coelsaId = transferData?.coelsaId || null

  if (!isOpen || !transferData) return null

  // Formatear moneda
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS'
    }).format(amount)
  }

  // Formatear fecha
  const formatDate = (dateStr) => {
    if (!dateStr) {
      const now = new Date()
      return now.toLocaleString('es-AR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
    }
    const date = new Date(dateStr)
    return date.toLocaleString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    })
  }

  // Formatear un monto numérico al estilo argentino (1.234,56)
  const formatAmountAR = (value) => {
    const num = parseFloat(value) || 0
    return num.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }

  // Generar y descargar comprobante (HTML print-friendly, mismo diseño que el de Movimientos)
  const handleDownloadComprobante = async () => {
    setDownloading(true)

    try {
      const amount = parseFloat(transferData.monto || 0)
      const directionGlyph = '↑'
      const tipoMovimiento = 'Transferencia enviada'

      // Nº de documento: COELSA > processId > timestamp
      const docNumber =
        (coelsaId && coelsaId !== '-' && coelsaId) ||
        transferData.processId ||
        `Z-${Date.now().toString(36).toUpperCase()}`

      const generatedAt = new Date().toLocaleString('es-AR', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit', hour12: false
      })

      const dateString = formatDate(transferData.fecha)
      const fileTitle = `comprobante-${docNumber}`.replace(/[^a-zA-Z0-9-]/g, '_')
      const safe = (v) => (v == null ? '' : String(v))

      const cvuOrigen = transferData.cvuOrigen || ''
      const cvuDestino = transferData.cvuDestino || ''
      const destNombre = transferData.destinatario?.nombre || ''
      const destCuit = transferData.destinatario?.cuit || ''
      const descripcion = transferData.descripcion || ''
      const processId = transferData.processId || ''

      const printWindow = window.open('', '_blank')
      if (!printWindow) {
        alert('No se pudo abrir la ventana del comprobante. Verificá que no haya un bloqueador de pop-ups.')
        return
      }

      printWindow.document.write(`
        <!DOCTYPE html>
        <html lang="es">
        <head>
          <meta charset="utf-8" />
          <title>${fileTitle}</title>
          <link rel="preconnect" href="https://fonts.googleapis.com">
          <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
          <link href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600&display=swap" rel="stylesheet">
          <style>
            *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }

            :root {
              --ink: #0B0B0F;
              --muted: #6B7280;
              --line: #E5E7EB;
              --line-strong: #111827;
              --paper: #FFFFFF;
              --accent: #1D4ED8;
              --accent-dark: #D05A15;
            }

            html, body {
              background: #F4F5F7;
              color: var(--ink);
              font-family: 'Plus Jakarta Sans', system-ui, -apple-system, Segoe UI, sans-serif;
              -webkit-font-smoothing: antialiased;
              font-feature-settings: "ss01", "cv11";
            }

            body {
              display: flex;
              justify-content: center;
              padding: 32px 16px 80px;
            }

            .sheet {
              width: 100%;
              max-width: 640px;
              background: var(--paper);
              padding: 56px 56px 40px;
              position: relative;
            }

            .sheet::before {
              content: "";
              position: absolute;
              top: 0;
              left: 0;
              right: 0;
              height: 4px;
              background: linear-gradient(90deg, var(--accent), var(--accent-dark));
            }

            .header {
              display: flex;
              align-items: flex-start;
              justify-content: space-between;
              gap: 32px;
              margin-bottom: 44px;
            }

            .title-block {
              display: flex;
              align-items: center;
              gap: 14px;
            }

            .title-mark {
              width: 10px;
              height: 36px;
              background: var(--accent);
              border-radius: 2px;
              flex-shrink: 0;
            }

            .title-text h1 {
              font-size: 26px;
              font-weight: 700;
              letter-spacing: -0.015em;
              line-height: 1.1;
              color: var(--ink);
            }

            .title-text span {
              display: block;
              margin-top: 4px;
              font-size: 9.5px;
              font-weight: 600;
              letter-spacing: 0.26em;
              text-transform: uppercase;
              color: var(--muted);
            }

            .doc-meta {
              text-align: right;
              font-family: 'JetBrains Mono', ui-monospace, monospace;
              font-size: 11px;
              line-height: 1.7;
              color: var(--ink);
              letter-spacing: 0.04em;
            }

            .doc-meta strong {
              display: block;
              font-weight: 700;
              color: var(--accent-dark);
              font-size: 10px;
              letter-spacing: 0.18em;
              text-transform: uppercase;
              margin-bottom: 4px;
              font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
            }

            .amount-block {
              border-top: 1px solid var(--line-strong);
              border-bottom: 1px solid var(--line);
              padding: 28px 0 32px;
              margin-bottom: 36px;
            }

            .amount-label {
              font-size: 10px;
              font-weight: 600;
              letter-spacing: 0.22em;
              text-transform: uppercase;
              color: var(--muted);
              margin-bottom: 14px;
            }

            .amount-value {
              display: flex;
              align-items: baseline;
              gap: 14px;
              font-variant-numeric: tabular-nums;
            }

            .amount-glyph {
              font-size: 28px;
              font-weight: 500;
              color: var(--accent);
              line-height: 1;
            }

            .amount-number {
              font-size: 56px;
              font-weight: 300;
              letter-spacing: -0.02em;
              line-height: 1;
            }

            .amount-currency {
              font-size: 18px;
              font-weight: 500;
              color: var(--muted);
              margin-bottom: 4px;
            }

            .amount-type {
              margin-top: 14px;
              font-size: 13px;
              font-weight: 500;
              color: var(--ink);
              letter-spacing: 0.01em;
            }

            .parties {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 0 36px;
              padding-bottom: 32px;
              border-bottom: 1px solid var(--line);
              margin-bottom: 28px;
            }

            .party-label {
              font-size: 9.5px;
              font-weight: 700;
              letter-spacing: 0.24em;
              text-transform: uppercase;
              color: var(--muted);
              margin-bottom: 10px;
            }

            .party-name {
              font-size: 15px;
              font-weight: 600;
              color: var(--ink);
              line-height: 1.35;
              margin-bottom: 10px;
              word-break: break-word;
            }

            .party-field {
              margin-top: 6px;
            }

            .party-field-label {
              display: block;
              font-size: 9px;
              font-weight: 500;
              letter-spacing: 0.18em;
              text-transform: uppercase;
              color: var(--muted);
              margin-bottom: 2px;
            }

            .party-field-value {
              font-family: 'JetBrains Mono', ui-monospace, monospace;
              font-size: 12px;
              color: var(--ink);
              word-break: break-all;
            }

            .description-block {
              padding-bottom: 24px;
              border-bottom: 1px solid var(--line);
              margin-bottom: 24px;
            }

            .description-label {
              font-size: 9.5px;
              font-weight: 700;
              letter-spacing: 0.24em;
              text-transform: uppercase;
              color: var(--muted);
              margin-bottom: 8px;
            }

            .description-value {
              font-size: 13px;
              font-weight: 400;
              color: var(--ink);
              line-height: 1.5;
              word-break: break-word;
            }

            .refs {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 0 36px;
              padding-bottom: 28px;
              border-bottom: 1px solid var(--line);
              margin-bottom: 28px;
            }

            .ref-label {
              font-size: 9.5px;
              font-weight: 700;
              letter-spacing: 0.24em;
              text-transform: uppercase;
              color: var(--muted);
              margin-bottom: 6px;
            }

            .ref-value {
              font-family: 'JetBrains Mono', ui-monospace, monospace;
              font-size: 12px;
              font-weight: 500;
              color: var(--ink);
              word-break: break-all;
            }

            .footer {
              display: flex;
              align-items: flex-end;
              justify-content: space-between;
              gap: 24px;
              font-size: 10.5px;
              color: var(--muted);
              line-height: 1.55;
            }

            .footer strong {
              color: var(--ink);
              font-weight: 600;
            }

            .footer-right {
              text-align: right;
              font-family: 'JetBrains Mono', ui-monospace, monospace;
              font-size: 10px;
              letter-spacing: 0.04em;
            }

            @media print {
              html, body { background: white; }
              body { padding: 0; }
              .sheet {
                max-width: none;
                width: 100%;
                padding: 24mm 22mm;
              }
            }

            @page {
              size: A4;
              margin: 0;
            }

            @media (max-width: 540px) {
              .sheet { padding: 32px 24px; }
              .parties, .refs { grid-template-columns: 1fr; gap: 20px 0; }
              .amount-number { font-size: 44px; }
              .header { flex-direction: column; gap: 18px; }
              .doc-meta { text-align: left; }
              .footer { flex-direction: column; align-items: flex-start; }
              .footer-right { text-align: left; }
            }
          </style>
        </head>
        <body>
          <article class="sheet">
            <header class="header">
              <div class="title-block">
                <div class="title-mark"></div>
                <div class="title-text">
                  <h1>Comprobante</h1>
                  <span>Constancia de operación</span>
                </div>
              </div>
              <div class="doc-meta">
                <strong>N.º documento</strong>
                ${safe(docNumber)}
              </div>
            </header>

            <section class="amount-block">
              <div class="amount-label">Monto</div>
              <div class="amount-value">
                <span class="amount-glyph">${directionGlyph}</span>
                <span class="amount-currency">$</span>
                <span class="amount-number">-${formatAmountAR(amount)}</span>
              </div>
              <div class="amount-type">${tipoMovimiento} · ${safe(dateString)}</div>
            </section>

            <section class="parties">
              <div>
                <div class="party-label">Origen</div>
                <div class="party-name">—</div>
                ${cvuOrigen ? `
                  <div class="party-field">
                    <span class="party-field-label">CVU</span>
                    <span class="party-field-value">${safe(cvuOrigen)}</span>
                  </div>
                ` : ''}
              </div>
              <div>
                <div class="party-label">Destino</div>
                <div class="party-name">${safe(destNombre) || '—'}</div>
                ${cvuDestino ? `
                  <div class="party-field">
                    <span class="party-field-label">CVU</span>
                    <span class="party-field-value">${safe(cvuDestino)}</span>
                  </div>
                ` : ''}
                ${destCuit ? `
                  <div class="party-field">
                    <span class="party-field-label">CUIT / DNI</span>
                    <span class="party-field-value">${safe(destCuit)}</span>
                  </div>
                ` : ''}
              </div>
            </section>

            ${descripcion ? `
              <section class="description-block">
                <div class="description-label">Descripción</div>
                <div class="description-value">${safe(descripcion)}</div>
              </section>
            ` : ''}

            <section class="refs">
              <div>
                <div class="ref-label">Referencia (Process ID)</div>
                <div class="ref-value">${safe(processId) || '—'}</div>
              </div>
              <div>
                <div class="ref-label">COELSA ID</div>
                <div class="ref-value">${(coelsaId && coelsaId !== '-') ? safe(coelsaId) : '—'}</div>
              </div>
            </section>

            <footer class="footer">
              <div>
                <strong>Constancia digital.</strong><br/>
                Este documento acredita la operación realizada. No requiere firma.
              </div>
              <div class="footer-right">
                Emitido<br/>${generatedAt}
              </div>
            </footer>
          </article>
          <script>
            window.onload = function () {
              document.title = '${fileTitle}';
              setTimeout(function () { window.print(); }, 200);
            };
          </script>
        </body>
        </html>
      `)
      printWindow.document.close()
    } catch (error) {
      console.error('Error generando comprobante:', error)
      alert('Error al generar el comprobante. Por favor, intente nuevamente.')
    } finally {
      setDownloading(false)
    }
  }

  return (
    <div className="transfer-success-overlay" onClick={onClose}>
      <div className="transfer-success-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header con icono de éxito */}
        <div className="success-modal-header">
          <div className="success-icon-container">
            <LuCircleCheck className="success-icon" />
          </div>
          <h2>¡Transferencia Exitosa!</h2>
          <button className="close-button" onClick={onClose}>
            <LuX />
          </button>
        </div>

        {/* Contenido */}
        <div className="success-modal-content">
          {/* Monto destacado */}
          <div className="amount-highlight">
            <span className="amount-label">Monto transferido</span>
            <span className="amount-value">{formatCurrency(transferData.monto)}</span>
          </div>

          {/* Detalles de la transferencia */}
          <div className="transfer-details">
            <div className="detail-group">
              <label>CVU Origen</label>
              <span className="cvu-text">{transferData.cvuOrigen}</span>
            </div>

            <div className="detail-group">
              <label>CVU Destino</label>
              <span className="cvu-text">{transferData.cvuDestino}</span>
              {transferData.destinatario?.nombre && (
                <span className="destinatario-name">{transferData.destinatario.nombre}</span>
              )}
            </div>

            {transferData.descripcion && (
              <div className="detail-group">
                <label>Descripción</label>
                <span>{transferData.descripcion}</span>
              </div>
            )}

            <div className="detail-group">
              <label>Fecha y hora</label>
              <span>{formatDate(transferData.fecha)}</span>
            </div>

            <div className="detail-group process-id">
              <label>Referencia (Process ID)</label>
              <span className="process-id-value">{transferData.processId || 'N/A'}</span>
            </div>

            {/* ⭐ COELSA ID */}
            <div className="detail-group coelsa-id-group">
              <label>Código COELSA</label>
              {coelsaId ? (
                <span className="coelsa-status success">{coelsaId}</span>
              ) : (
                <span className="coelsa-status pending">No disponible</span>
              )}
            </div>
          </div>
        </div>

        {/* Botones de acción */}
        <div className="success-modal-actions">
          <button 
            className="btn-download-comprobante"
            onClick={handleDownloadComprobante}
            disabled={downloading}
          >
            {downloading ? (
              <>
                <LuLoader className="spinner" />
                Generando...
              </>
            ) : (
              <>
                <LuDownload />
                Descargar Comprobante
              </>
            )}
          </button>

          <button 
            className="btn-copy-datos"
            onClick={() => {
              const text = `MONTO TRANSFERIDO\n${formatCurrency(transferData.monto)}\n\nCVU ORIGEN\n${transferData.cvuOrigen}\n\nCVU DESTINO\n${transferData.cvuDestino}\n\n${transferData.destinatario?.nombre || ''}\n\nDESCRIPCIÓN\n${transferData.descripcion || 'Pago de Servicios'}\n\nFECHA Y HORA\n${formatDate(transferData.fecha)}\n\nREFERENCIA (PROCESS ID)\n${transferData.processId || 'N/A'}\n\nCÓDIGO COELSA\n${coelsaId || 'No disponible'}`
              navigator.clipboard.writeText(text)
              setCopied(true)
              setTimeout(() => setCopied(false), 2000)
            }}
          >
            <LuCopy />
            {copied ? 'Copiado!' : 'Copiar Datos'}
          </button>
          
          <button className="btn-close-modal" onClick={onClose}>
            Cerrar
          </button>
        </div>
      </div>
    </div>
  )
}

export default TransferSuccessModal
